import { TONE_NAMES, type ToneName, type ToneRailOverrides, type ToneSlots } from './tokens';

/**
 * Per-tone hue and the lightness its solid fill wants.
 *
 * Hue is fixed across themes — danger has to stay red-ish or the UI lies. What
 * a theme actually varies is chroma (how saturated the whole system reads) and
 * a small shared hue nudge toward its temperature.
 *
 * `baseL` differs per tone because equal oklch lightness does NOT look equally
 * strong across hues at the extremes: yellow at L 0.64 reads as mud, so warning
 * sits higher and pays for it with dark text in the `on` slot.
 */
const TONE_SPEC: Record<ToneName, { hue: number; baseL: number; baseC: number; inkL: number; inkHue?: number }> = {
  danger:  { hue: 22,  baseL: 0.635, baseC: 0.205, inkL: 0.475 },
  caution: { hue: 48,  baseL: 0.685, baseC: 0.175, inkL: 0.505 },
  warning: { hue: 78,  baseL: 0.760, baseC: 0.150, inkL: 0.520, inkHue: 68 },
  success: { hue: 152, baseL: 0.640, baseC: 0.135, inkL: 0.480 },
  fresh:   { hue: 196, baseL: 0.660, baseC: 0.110, inkL: 0.490, inkHue: 205 },
  info:    { hue: 252, baseL: 0.600, baseC: 0.150, inkL: 0.480, inkHue: 258 },
  accent1: { hue: 272, baseL: 0.575, baseC: 0.175, inkL: 0.470 },
  accent2: { hue: 302, baseL: 0.620, baseC: 0.170, inkL: 0.485 },
  accent3: { hue: 335, baseL: 0.635, baseC: 0.185, inkL: 0.490 },
  neutral: { hue: 310, baseL: 0.620, baseC: 0.022, inkL: 0.470 },
};

export interface ToneOptions {
  mode: 'light' | 'dark';
  /**
   * Multiplies every chroma. 1 = reference. Below 1 gives a dusty, muted
   * system; above 1 gives candy. Neutral is exempt — it takes `neutralChroma`.
   */
  chroma?: number;
  /** Degrees added to every hue. Small values (<=8) pull the whole set warm or cool. */
  hueShift?: number;
  /** Hue the grays carry, so "gray" matches the theme's ground instead of reading cold. */
  neutralHue?: number;
  /** How much color the grays carry. Keep tiny; 0.02-0.05 is the useful range. */
  neutralChroma?: number;
  /**
   * Dark mode only: the lightness of the ground the tones will sit on.
   *
   * Chip washes are placed a fixed step ABOVE it rather than at a fixed
   * lightness, so a scheme can lift its ground without its chips dissolving
   * into the card behind them. `ink` rises with it too, since the text has to
   * stay clear of a wash that just got lighter.
   *
   * Defaults to 0.196 — the value the first dark scheme was tuned against —
   * so omitting it reproduces the original numbers exactly.
   */
  groundL?: number;
}

const round = (n: number, places = 3) => Number(n.toFixed(places));

/** Clamp so a chroma multiplier can't push a value out of the sRGB gamut. */
const clampC = (c: number) => round(Math.max(0, Math.min(c, 0.33)));

function slotsFor(tone: ToneName, opts: ToneOptions): ToneSlots {
  const spec = TONE_SPEC[tone];
  const isNeutral = tone === 'neutral';
  const mul = isNeutral ? 1 : (opts.chroma ?? 1);
  const shift = opts.hueShift ?? 0;

  const hue = isNeutral ? (opts.neutralHue ?? spec.hue) : round(spec.hue + shift, 1);
  const inkHue = isNeutral ? hue : round((spec.inkHue ?? spec.hue) + shift, 1);
  const baseC = isNeutral ? (opts.neutralChroma ?? spec.baseC) : clampC(spec.baseC * mul);

  if (opts.mode === 'dark') {
    // On a dark ground the roles invert: the pale wash becomes a deep tint of
    // the tone, and `ink` has to be LIGHTER than the surroundings rather than
    // darker. `on` flips to dark text because `base` is now the bright thing
    // in the room.
    //
    // Everything is expressed as a step from `groundL` rather than as an
    // absolute lightness. The offsets below are the original absolutes minus
    // the original 0.196 ground, so the default reproduces them exactly while
    // a lifted ground carries its whole tone set up with it.
    const g = opts.groundL ?? 0.196;
    const lift = g - 0.196;
    return {
      base: `oklch(${round(Math.min(spec.baseL + 0.06, 0.78))} ${clampC(baseC * 0.92)} ${hue})`,
      ink: `oklch(${round(0.80 + lift * 0.7 - (isNeutral ? 0.06 : 0))} ${clampC(baseC * (isNeutral ? 1.2 : 0.66))} ${inkHue})`,
      surface: `oklch(${round(g + (isNeutral ? 0.114 : 0.129))} ${clampC(baseC * (isNeutral ? 1.6 : 0.42))} ${hue})`,
      line: `oklch(${round(g + 0.209)} ${clampC(baseC * (isNeutral ? 1.5 : 0.46))} ${hue})`,
      // Text on the solid fill stays dark whatever the ground does — `base` is
      // the bright thing in the room either way.
      on: `oklch(0.185 ${clampC(baseC * 0.2)} ${hue})`,
    };
  }

  /**
   * Which way `on` goes — the text that sits on the solid `base` fill.
   *
   * This is where oklch earns its keep: because L is perceptually uniform, the
   * white-vs-dark crossover is a single threshold rather than a per-hue
   * luminance calculation.
   *
   * Above the threshold (warning, caution, fresh, success, danger, accent3) a
   * deep tint of the tone clears 4.5:1 comfortably — worth the slightly
   * unconventional look of dark text on a red fill.
   *
   * Below it (info, accent1, accent2, neutral) near-white wins but only lands
   * at 3.5-4.5:1. That is the same ratio `bg-blue-500 text-white` ships at in
   * every framework, and it meets AA for the large/bold text a solid-fill
   * label always is — but it is NOT AA for body copy. Reaching 4.5:1 there
   * would mean darkening `base`, which would also dull every `/10` wash and
   * status dot drawn from it; those have real users today and this slot does
   * not. If you ever put small text on a solid fill, darken that tone's base
   * deliberately rather than assuming this slot covers you.
   */
  const onIsDark = spec.baseL >= 0.63;

  return {
    base: `oklch(${spec.baseL} ${baseC} ${hue})`,
    ink: `oklch(${spec.inkL} ${clampC(baseC * (isNeutral ? 1.27 : 0.86))} ${inkHue})`,
    surface: `oklch(${isNeutral ? 0.945 : 0.948} ${clampC(baseC * (isNeutral ? 0.55 : 0.21))} ${hue})`,
    line: `oklch(${isNeutral ? 0.89 : 0.885} ${clampC(baseC * (isNeutral ? 0.82 : 0.34))} ${hue})`,
    on: onIsDark ? `oklch(0.225 ${clampC(baseC * 0.34)} ${inkHue})` : `oklch(0.99 0.005 ${hue})`,
  };
}

/** The full tone set for a theme. */
export function buildTones(opts: ToneOptions): Record<ToneName, ToneSlots> {
  return Object.fromEntries(
    TONE_NAMES.map((tone) => [tone, slotsFor(tone, opts)]),
  ) as Record<ToneName, ToneSlots>;
}

export interface RailToneOptions extends ToneOptions {
  /**
   * Lightness of the rail itself. The washes are placed a fixed step above it
   * so a chip reads as raised off the rail rather than punched into it,
   * whatever the rail's own darkness.
   */
  railL: number;
  /** The rail's hue, so tinted washes sit in the rail's color family. */
  railHue: number;
}

/**
 * Tone overrides for content rendered on the dark nav rail.
 *
 * Only ink/surface/line move: the solid `base` fill and the `on` text that
 * rides it are already high-contrast against anything.
 */
export function buildRailTones(opts: RailToneOptions): Record<ToneName, ToneRailOverrides> {
  const mul = opts.chroma ?? 1;
  const shift = opts.hueShift ?? 0;

  return Object.fromEntries(
    TONE_NAMES.map((tone) => {
      const spec = TONE_SPEC[tone];
      const isNeutral = tone === 'neutral';
      const hue = isNeutral ? (opts.neutralHue ?? opts.railHue) : round(spec.hue + shift, 1);
      const c = isNeutral ? (opts.neutralChroma ?? 0.03) : clampC(spec.baseC * mul);

      return [tone, {
        // Warning/caution sit naturally lighter, and dropping them to a common
        // lightness would make amber read brown on plum.
        ink: `oklch(${round(Math.min(0.74 + (spec.baseL - 0.62) * 0.55, 0.86))} ${clampC(c * (isNeutral ? 1.1 : 0.72))} ${hue})`,
        surface: `oklch(${round(opts.railL + 0.135)} ${clampC(c * (isNeutral ? 1.6 : 0.52))} ${hue})`,
        line: `oklch(${round(opts.railL + 0.215)} ${clampC(c * (isNeutral ? 1.7 : 0.56))} ${hue})`,
      }] as const;
    }),
  ) as Record<ToneName, ToneRailOverrides>;
}
